import React, { useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ExternalLink, Quote, Star, ShoppingCart } from 'lucide-react';
import { books } from '../mock/data';

const Books = () => {
  const [selectedBook, setSelectedBook] = useState(null);

  const BookCard = ({ book }) => (
    <Card className="group cursor-pointer transform transition-all duration-300 hover:scale-105 hover:shadow-2xl border-sage-200 bg-gradient-to-br from-white to-sage-50">
      <CardContent className="p-0">
        <div className="relative overflow-hidden rounded-t-lg">
          <img
            src={book.cover}
            alt={`Capa do livro ${book.title}`}
            className="w-full h-80 object-cover transition-transform duration-300 group-hover:scale-110"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
        </div>
        
        <div className="p-6">
          <h3 className="text-xl font-bold text-sage-800 mb-2">{book.title}</h3>
          
          {book.contentWarning && (
            <Badge variant="outline" className="mb-3 text-xs border-rose-300 text-rose-600">
              {book.contentWarning}
            </Badge>
          )}
          
          <p className="text-sage-600 text-sm mb-4 line-clamp-3">
            {book.synopsis}
          </p>
          
          <div className="flex flex-wrap gap-2 mb-4">
            {Object.entries(book.links).map(([platform, url]) => (
              <Button
                key={platform}
                size="sm"
                variant="outline"
                onClick={(e) => {
                  e.stopPropagation();
                  window.open(url, '_blank');
                }}
                className="text-xs capitalize border-sage-300 text-sage-700 hover:bg-sage-100"
              >
                <ExternalLink size={12} className="mr-1" />
                {platform}
              </Button>
            ))}
          </div>
          
          <Button
            onClick={() => setSelectedBook(book)}
            className="w-full bg-rose-500 hover:bg-rose-600 text-white transition-colors duration-200"
          >
            Ver Detalhes
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <section id="books" className="py-20 bg-gradient-to-br from-sage-50 via-white to-rose-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-sage-800 mb-6">
            Meus Livros
          </h2>
          <p className="text-xl text-sage-600 max-w-3xl mx-auto mb-8">
            Descubra mundos de fantasia, romance e aventura onde o amor sáfico 
            e personagens fortes ganham vida em cada página
          </p>
          <div className="w-24 h-1 bg-gradient-to-r from-rose-400 to-sage-400 mx-auto"></div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {books.map((book) => (
            <BookCard key={book.id} book={book} />
          ))}
        </div>

        {/* Book Details Modal */}
        {selectedBook && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl max-w-4xl max-h-[90vh] overflow-y-auto shadow-2xl">
              <div className="relative">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSelectedBook(null)}
                  className="absolute top-4 right-4 z-10 bg-white/80 backdrop-blur-sm hover:bg-white"
                >
                  ✕
                </Button>
                
                <div className="grid md:grid-cols-2 gap-8 p-8">
                  <div>
                    <img
                      src={selectedBook.cover}
                      alt={`Capa do livro ${selectedBook.title}`}
                      className="w-full max-w-sm mx-auto rounded-lg shadow-lg"
                    />
                  </div>
                  
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-3xl font-bold text-sage-800 mb-2">
                        {selectedBook.title}
                      </h3>
                      {selectedBook.contentWarning && (
                        <Badge variant="outline" className="border-rose-300 text-rose-600">
                          {selectedBook.contentWarning}
                        </Badge>
                      )}
                    </div>
                    
                    <p className="text-sage-700 leading-relaxed">
                      {selectedBook.synopsis}
                    </p>
                    
                    {selectedBook.blurbs && (
                      <div className="space-y-4">
                        <h4 className="text-lg font-semibold text-sage-800">O que dizem sobre o livro</h4>
                        {selectedBook.blurbs.map((blurb, index) => (
                          <div key={index} className="bg-sage-50 p-4 rounded-lg border-l-4 border-rose-400">
                            <Quote className="text-rose-400 mb-2" size={20} />
                            <p className="text-sage-700 italic mb-2">"{blurb.text}"</p>
                            <p className="text-sage-600 text-sm font-medium">— {blurb.author}</p>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    <div className="space-y-3">
                      <h4 className="text-lg font-semibold text-sage-800">Onde encontrar</h4>
                      <div className="flex flex-wrap gap-2">
                        {Object.entries(selectedBook.links).map(([platform, url]) => (
                          <Button
                            key={platform}
                            onClick={() => window.open(url, '_blank')}
                            className="bg-sage-600 hover:bg-sage-700 text-white capitalize"
                          >
                            <ShoppingCart size={16} className="mr-2" />
                            {platform}
                          </Button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default Books;